import { AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { LoginRequest, PublicService, UserRegistrationVo, UserRequest } from '../../../core/services/public.service';
import { SimpleCodeValue } from '../../../core/models/code-value.model';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { catchError, map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import * as CryptoJS from 'crypto-js';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReportService } from '../../../core/services/report.service';

@Component({
  selector: 'app-registration',
  standalone: true,
  imports: [
    MatStepperModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSelectModule,
    ReactiveFormsModule,
    CommonModule,
    MatIconModule
  ],
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
})
export class RegistrationComponent implements OnInit, AfterViewInit, OnChanges {
  
  private snackBar = inject(MatSnackBar);
  personalDetailsForm: FormGroup;
  locationDetailsForm: FormGroup;
  loginDetailsForm: FormGroup;
  hidePassword = true;
  hideConfirmPassword = true;

  isUsernameValid: boolean | null = null;
  successPage: boolean = false;

  @Input() userMobileNumber: string = '';
  @Input() userEmail: string = '';
  @Input() token: string = '';
  @Input() selectedStation: string = '';

  zones: SimpleCodeValue[] = [];
  areas: SimpleCodeValue[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private publicService: PublicService,
    private cdr: ChangeDetectorRef,
    private reportService: ReportService
  ) {
    this.personalDetailsForm = this.fb.group({
      rank: ['', [Validators.pattern('^[a-zA-Z ]*$')]], // Only alphabets and spaces
      firstName: ['User', [Validators.required, Validators.pattern('^[a-zA-Z ]*$')]], // Required and only alphabets and spaces
      lastName: ['User', [Validators.pattern('^[a-zA-Z ]*$')]], // Only alphabets and spaces
      phoneNumber: [null],
      mobileNumber: [{ value: '', disabled: true }, [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      emailAddress: ['', [Validators.required, Validators.email, Validators.pattern("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,4}$")]],
      allowSms: [false, [Validators.required]],
      allowEmail: [false, [Validators.required]],
    });

    this.locationDetailsForm = this.fb.group({
      zoneId: [this.selectedStation, Validators.required],
      areaName: [null, Validators.required],
      quarterNo: [''],
      blockNumber: ['', Validators.required],
    });

    this.loginDetailsForm = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(12), Validators.pattern('^[a-zA-Z0-9]+$')], [this.validateUsername.bind(this)]], // Added async validator
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(12),
          Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$'),
        ],
      ],
      confirmPassword: ['', Validators.required]
    }, {
      validators: this.matchPasswords('password', 'confirmPassword')
    });
  }

  ngOnInit(): void {
    this.getZones();
    this.locationDetailsForm.controls['zoneId'].disable();
    this.personalDetailsForm.controls['rank'].disable();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userMobileNumber'] && changes['userMobileNumber'].currentValue !== changes['userMobileNumber'].previousValue) {
      this.personalDetailsForm.patchValue({
        mobileNumber: this.userMobileNumber
      });
      this.personalDetailsForm.get('mobileNumber')?.disable();
    }

    if (changes['userEmail'] && changes['userEmail'].currentValue !== changes['userEmail'].previousValue) {
      this.personalDetailsForm.patchValue({
        emailAddress: this.userEmail
      });
      this.personalDetailsForm.get('emailAddress')?.disable();
    }

    if (changes['selectedStation'] && changes['selectedStation'].currentValue !== changes['selectedStation'].previousValue) {
      console.log('Selected Station in Registration:', this.selectedStation);
      this.locationDetailsForm.patchValue({
        zoneId: this.selectedStation
      });
      this.onZoneSelect(this.selectedStation);
    }
  }

  // Async validator to check if the username is available
  validateUsername(control: any): Observable<any> {
    return this.publicService.checkUsernameAvailability(control.value, this.token).pipe(
      map(response => {
        this.isUsernameValid = response.valid;
        return response.valid ? null : { usernameTaken: true };
      }),
      catchError(() => {
        return [{ usernameTaken: true }];
      })
    );
  }

  getZones() {
    this.publicService.getEnabledZones(this.token).subscribe({
      next: (response) => {
        this.zones = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  onZoneSelect(zoneId: any) {
    this.getAreasByZoneId(zoneId);
  }

  getAreasByZoneId(zoneId: string) {
    this.publicService.getAreasByZoneId(zoneId, this.token).subscribe({
      next: (response) => {
        this.areas = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  matchPasswords(password: string, confirmPassword: string) {
    return (formGroup: FormGroup) => {
      const pass = formGroup.controls[password];
      const confirmPass = formGroup.controls[confirmPassword];
      if (pass.value !== confirmPass.value) {
        confirmPass.setErrors({ mustMatch: true });
      } else {
        confirmPass.setErrors(null);
      }
    };
  }

  onSubmit() {
    if (this.personalDetailsForm.valid && this.locationDetailsForm.valid && this.loginDetailsForm.valid) {
      
      
      // console.log('Form submitted', this.personalDetailsForm.value, this.locationDetailsForm.value, this.loginDetailsForm.value);

      const userName: string = this.loginDetailsForm.value['userName'];
      const password: string = this.loginDetailsForm.value['password'];

      const secret = environment.ivBase64Secret;
      const sha256Key = CryptoJS.SHA256(secret);
      const iv = CryptoJS.lib.WordArray.random(16);
      const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
      const encryptedUsername = CryptoJS.AES.encrypt(userName, sha256Key, { iv: iv }).toString();
      const encryptedPassword = CryptoJS.AES.encrypt(password, sha256Key, { iv: iv }).toString();

      const loginRequest: LoginRequest = {
        userName: encryptedUsername,
        password: encryptedPassword,
        instance: ivBase64
      };

      const userRequest: UserRequest = {
        allowEmail: true,
        allowSmsFlag: true,
        approvedAreaName: null,
        approvedBlockNo: null,
        approvedQtrNo: null,
        appZone: null,
        areaName: this.locationDetailsForm.value['areaName'],
        blockNo: this.locationDetailsForm.value['blockNumber'],
        emailAddress: this.personalDetailsForm.value['emailAddress'],
        emailFlag: true,
        encypretedPassword: encryptedPassword,
        firstName: this.personalDetailsForm.value['firstName'],
        ivrLanguage: null,
        lastName: this.personalDetailsForm.value['lastName'],
        lookupType: '',
        mobileNumber: this.userMobileNumber,
        phoneNumber: null,
        qtrNo: this.locationDetailsForm.value['quarterNo'],
        rank: this.personalDetailsForm.value['rank'],
        resPicUrl: null,
        smsFlag: true,
        status: 'PENDING',
        userId: null,
        userName: encryptedUsername
      }

      const zoneId: string = this.selectedStation;
      const userRegistrationData: UserRegistrationVo = {
        loginRequest: loginRequest,
        userRequest: userRequest,
        token: this.token,
        zoneId: Number(zoneId)
      };

      // console.log(userRegistrationData);

      this.publicService.registerUserRequest(userRegistrationData).subscribe({
        next: (response) => {
          if (response.valid) {
            this.openSnackBar(response.message, 'Okay');
            this.downloadForm();
            this.successPage = true;
          } else {
            this.openSnackBar(response.message, 'Okay');
          }
        },
        error: (error) => {
          console.error('POST error:', error);
        },
      });
    }
  }

  downloadForm() {
    const userName: string = this.loginDetailsForm.value['userName'];
    this.reportService.downloadUserRequestPDF(userName).subscribe(
      (response) => {
        const blob = new Blob([response.body!], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'report.pdf';
        a.click();
        window.URL.revokeObjectURL(url); // Clean up
      },
      (error) => {
        console.error('PDF download failed', error);
      }
    );
  }

  backToLogin() {
    this.router.navigate(['/app/login'], { replaceUrl: true });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 3 * 1000 }); // 3 seconds
  }
}
